/**
 * Browse, for somebody who knows the department and not the title.
 *
 * The organization's own departments and categories, as two plain lists.
 * Choosing one shows the published policies filed under it; choosing it
 * again, or "All", shows everything. There are no folders because the
 * taxonomy has none, and inventing them would be inventing a place to look
 * that nobody filed anything in.
 */
import { GroupCard } from '../../../shared/GroupCard';
import type { TaxonomyItem } from '../../admin/useTaxonomy';
import styles from './Library.module.css';

function BrowseList({ title, all, items, value, onChoose }: {
  title: string;
  all: string;
  items: TaxonomyItem[];
  value: string | null;
  onChoose: (id: string | null) => void;
}) {
  // Every one, in full. A list cut off by a scrolling box hides that there is
  // more, and one folded behind "more" hides the very department somebody
  // came for; a hospital has a few dozen, and the page scrolls anyway.
  const active = items.filter((i) => i.isActive);
  if (active.length === 0) return null;
  return (
    <GroupCard title={title} list level={2}>
      <ul className={styles.browseList} style={{ margin: '0 calc(-1 * var(--sp-4))' }}>
        <li>
          <button type="button" className={value === null ? `${styles.browseLink} ${styles.browseOn}` : styles.browseLink} aria-pressed={value === null} onClick={() => onChoose(null)}>
            {all}
          </button>
        </li>
        {active.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={value === item.id ? `${styles.browseLink} ${styles.browseOn}` : styles.browseLink}
              aria-pressed={value === item.id}
              onClick={() => onChoose(value === item.id ? null : item.id)}
            >
              {item.name}
            </button>
          </li>
        ))}
      </ul>
    </GroupCard>
  );
}

export function BrowsePanel({ departments, categories, department, category, onChange }: {
  departments: TaxonomyItem[];
  categories: TaxonomyItem[];
  department: string | null;
  category: string | null;
  onChange: (patch: { department?: string | null; category?: string | null }) => void;
}) {
  return (
    <aside className={styles.browse} aria-label="Browse policies">
      <BrowseList title="Browse by department" all="All departments" items={departments} value={department} onChoose={(id) => onChange({ department: id })} />
      <BrowseList title="Browse by category" all="All categories" items={categories} value={category} onChoose={(id) => onChange({ category: id })} />
    </aside>
  );
}
